import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Link,
  CircularProgress,
  Alert,
  Fade,
  Slide,
  Zoom,
  Grow,
  Box
} from '@mui/material';
import { 
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import Illustration from '../img/login-illustration.jpg'
import { authService } from '../../services/auth';

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Registration = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!form.password) {
      newErrors.password = 'Password is required';
    } else if (form.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const data = await authService.registerTechnician(form);
      alert('Registration successful! Please login.');
      navigate('/login');
    } catch (error) {
      setErrors({ 
        server: error.message || 'An error occurred during registration' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      py: 8
    }}>
      <Grow in={true} timeout={800}>
        <Grid container spacing={6} sx={{ alignItems: 'center' }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={6} sx={{
              p: 6,
              borderRadius: 4,
              background: 'linear-gradient(-45deg, #f3f4f6, #ffffff, #f8f9fa)',
              backgroundSize: '400% 400%',
              animation: `${gradientAnimation} 10s ease infinite`,
              position: 'relative',
              overflow: 'hidden',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                background: 'linear-gradient(45deg, #2196f3, #e91e63)',
                borderRadius: '50%',
                filter: 'blur(80px)',
                opacity: 0.15,
              }
            }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Slide in direction="down" timeout={500}>
                  <Typography variant="h3" component="h1" gutterBottom sx={{ 
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #2196f3 30%, #e91e63 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Create Account
                  </Typography>
                </Slide>

                <Fade in timeout={1000}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Join us today to get started
                  </Typography>
                </Fade>
              </Box>

              {errors.server && (
                <Slide in direction="right" timeout={300}>
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                    {errors.server}
                  </Alert>
                </Slide>
              )}

              <form onSubmit={handleRegister}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {[
                    {
                      field: 'name',
                      label: 'Full Name',
                      icon: <PersonIcon />,
                      type: 'text',
                      error: errors.name
                    },
                    {
                      field: 'email',
                      label: 'Email Address',
                      icon: <EmailIcon />,
                      type: 'email',
                      error: errors.email
                    },
                    {
                      field: 'password',
                      label: 'Password (min 6 characters)',
                      icon: <LockIcon />,
                      type: 'password',
                      extra: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                      error: errors.password
                    },
                    {
                      field: 'confirmPassword',
                      label: 'Confirm Password',
                      icon: <LockIcon />,
                      type: 'password',
                      extra: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                      error: errors.confirmPassword
                    }
                  ].map((item, index) => (
                    <Grow 
                      key={item.field}
                      in={true}
                      timeout={index * 200 + 500}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      <TextField
                        fullWidth
                        required
                        name={item.field}
                        type={item.type === 'password' ? 
                          (item.field === 'password' ? 
                            (showPassword ? 'text' : 'password') : 
                            (showConfirmPassword ? 'text' : 'password')) : 
                          item.type}
                        label={item.label}
                        value={form[item.field]}
                        onChange={handleChange}
                        error={Boolean(item.error)}
                        helperText={item.error}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {item.icon}
                            </InputAdornment>
                          ),
                          endAdornment: item.extra,
                          sx: { borderRadius: 3 }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            transition: 'all 0.3s',
                            '&.Mui-focused': {
                              boxShadow: '0 0 0 2px rgba(33,150,243,0.2)'
                            }
                          }
                        }}
                      />
                    </Grow>
                  ))}

                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontSize: 16,
                      background: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : 'Register'}
                  </Button>

                  <Typography variant="body2" sx={{ textAlign: 'center' }}>
                    Already have an account?{' '}
                    <Link href="/login" sx={{ fontWeight: 600 }}>
                      Login here
                    </Link>
                  </Typography>
                </Box>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Zoom in={true} style={{ transitionDelay: '300ms' }}>
              <Box
                component="img"
                src={Illustration}
                alt="Registration Illustration"
                sx={{ 
                  width: '100%',
                  height: 'auto',
                  maxWidth: 600,
                  ml: 'auto'
                }}
              />
            </Zoom>
          </Grid>
        </Grid>
      </Grow>
    </Container>
  );
};

export default Registration;